import { useCallback, useState, useRef } from 'react';
import { useApi } from './useApi';
import type {
  PlanningSession,
  TicketDraft,
  ChatMessage,
  FileAttachment,
  CreateSessionInput,
  UpdateTicketDraftInput,
  JiraCreationResults,
} from '@pantheon/shared';

interface SessionWithTickets extends PlanningSession {
  output?: PlanningSession['output'] & { tickets?: TicketDraft[] };
}

interface UseHermesSessionReturn {
  session: SessionWithTickets | null;
  tickets: TicketDraft[];
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  createSession: (input: CreateSessionInput) => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  updateTicket: (draftId: string, input: UpdateTicketDraftInput) => Promise<void>;
  refine: (message: string, attachments?: FileAttachment[]) => Promise<void>;
  regenerate: () => Promise<void>;
  stop: () => Promise<void>;
  approve: () => Promise<void>;
  reset: () => void;
}

export function useHermesSession(): UseHermesSessionReturn {
  const { post, get, put } = useApi();
  const [session, setSession] = useState<SessionWithTickets | null>(null);
  const [tickets, setTickets] = useState<TicketDraft[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResult = useCallback(
    (result: { success: boolean; data?: SessionWithTickets; error?: { message: string } }) => {
      if (result.success && result.data) {
        setSession(result.data);
        setTickets(result.data.output?.tickets ?? []);
        setMessages(result.data.messages ?? []);
        setError(null);
      } else {
        setError(result.error?.message ?? 'Unknown error');
      }
    },
    [],
  );

  // Which session id is actively being polled — guards against duplicate loops
  // (re-render / re-mount) and lets a poll loop cancel itself when superseded.
  const pollingRef = useRef<string | null>(null);

  const pollSession = useCallback(
    (sessionId: string) => {
      if (pollingRef.current === sessionId) return; // already polling this one
      pollingRef.current = sessionId;
      const poll = async () => {
        if (pollingRef.current !== sessionId) return; // superseded or stopped
        try {
          const result = await get<SessionWithTickets>(`/hermes/sessions/${sessionId}`);
          if (result.success && result.data) {
            setSession(result.data);
            setTickets(result.data.output?.tickets ?? []);
            setMessages(result.data.messages ?? []);

            const status = result.data.status;
            if (status === 'reviewing' || status === 'completed' || status === 'failed') {
              pollingRef.current = null;
              setLoading(false);
              if (status === 'failed') setError('Session processing failed');
              return; // terminal — stop polling
            }
          }
        } catch {
          // Transient network/API hiccup — do NOT kill the loop (the old code did,
          // which is why a session could sit on "generating" until a manual reload).
        }
        if (pollingRef.current === sessionId) setTimeout(poll, 3000);
      };
      void poll();
    },
    [get],
  );

  const createSession = useCallback(
    async (input: CreateSessionInput) => {
      setLoading(true);
      setError(null);
      try {
        const result = await post<SessionWithTickets>('/hermes/sessions', input);
        if (result.success && result.data) {
          setSession(result.data);
          // Session created — start polling for completion
          pollSession(result.data.sessionId);
        } else {
          setError(result.error?.message ?? 'Failed to create session');
          setLoading(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create session');
        setLoading(false);
      }
    },
    [post, pollSession],
  );

  const loadSession = useCallback(
    async (sessionId: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await get<SessionWithTickets>(`/hermes/sessions/${sessionId}`);
        handleResult(result);
        // If the session is still processing (opened via a deep link, or the page was
        // reloaded mid-run), resume polling so it advances to reviewing on its own —
        // no manual refresh. pollSession keeps loading=true until it reaches a terminal
        // state, so the loading indicator stays honest.
        const status = result.success ? result.data?.status : undefined;
        if (status && ['drafting', 'ingesting_context', 'generating', 'creating_tickets'].includes(status)) {
          pollSession(sessionId);
          return;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load session');
      }
      setLoading(false);
    },
    [get, handleResult, pollSession],
  );

  const updateTicket = useCallback(
    async (draftId: string, input: UpdateTicketDraftInput) => {
      if (!session) return;
      const result = await put<TicketDraft>(
        `/hermes/sessions/${session.sessionId}/tickets/${draftId}`,
        input,
      );
      if (result.success && result.data) {
        setTickets((prev) =>
          prev.map((t) => (t.draftId === draftId ? result.data! : t)),
        );
      }
    },
    [session, put],
  );

  const regenerate = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const result = await post<{ status: string }>(
        `/hermes/sessions/${session.sessionId}/regenerate`,
        {},
      );
      if (result.success) {
        // Start polling for completion (same as createSession)
        pollSession(session.sessionId);
      } else {
        setError(result.error?.message ?? 'Failed to regenerate');
        setLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate');
      setLoading(false);
    }
  }, [session, post, pollSession]);

  const approve = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const result = await post<SessionWithTickets>(
        `/hermes/sessions/${session.sessionId}/approve`,
        {},
      );
      if (result.success && result.data) {
        // Only update session metadata — don't re-set tickets/messages
        // to avoid DOM re-renders that cause scroll jumps
        setSession(result.data);
        setError(null);
      } else {
        setError(result.error?.message ?? 'Failed to approve');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setLoading(false);
    }
  }, [session, post]);

  const refine = useCallback(
    async (message: string, attachments?: FileAttachment[]) => {
      if (!session) return;
      setLoading(true);
      setError(null);

      // Optimistically add user message to chat
      setMessages((prev) => [...prev, { role: 'user' as const, content: message, timestamp: new Date().toISOString(), attachments }]);

      try {
        const result = await post<{ status: string }>(
          `/hermes/sessions/${session.sessionId}/refine`,
          { message, attachments },
        );
        if (result.success) {
          // Start polling for the refined results
          pollSession(session.sessionId);
        } else {
          setError(result.error?.message ?? 'Failed to refine');
          setLoading(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to refine');
        setLoading(false);
      }
    },
    [session, post, pollSession],
  );

  const stop = useCallback(async () => {
    pollingRef.current = null; // halt any active poll loop
    if (session) {
      await post(`/hermes/sessions/${session.sessionId}/stop`, {});
    }
    setSession(null);
    setTickets([]);
    setMessages([]);
    setLoading(false);
    setError(null);
  }, [session, post]);

  const reset = useCallback(() => {
    pollingRef.current = null; // halt any active poll loop
    setSession(null);
    setTickets([]);
    setMessages([]);
    setLoading(false);
    setError(null);
  }, []);

  return {
    session,
    tickets,
    messages,
    loading,
    error,
    createSession,
    loadSession,
    updateTicket,
    refine,
    regenerate,
    approve,
    stop,
    reset,
  };
}
